import {
  ConversationId,
  ConversationThreadId,
  MessageAttachmentId,
  MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { DraftResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { Attachment } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/attachment";
import { useAsyncDebounce } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { conversationActionIds } from "@redotech/redo-web/actions/conversation-actions/action-ids";
import { messageInputScope } from "@redotech/redo-web/actions/conversation-actions/scope-trees";
import { useActiveContext } from "@redotech/redo-web/actions/use-active-context";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { formatShortcut } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { messageInputShortcuts } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { Option } from "effect";
import type { Value } from "platejs";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useDeleteDraftAction,
  useSendMessageAction,
} from "../actions/use-conversation-actions";
import { useDraft, useDraftMutations } from "../hooks/use-draft";
import type { ReplyMode } from "../hooks/use-reply-mode";
import { useReplyMode } from "../hooks/use-reply-mode";
import { useSendMessage } from "../hooks/use-send-message";
import { EmailToolbar } from "./message-editor/email-toolbar";
import { MessageEditor } from "./message-editor/message-editor";
import { useMessageEditor } from "./message-editor/use-message-editor";
import { ReplyModeDropdown } from "./reply-mode-dropdown";

export const MessageInput = memo(function MessageInput({
  conversationId,
  threadId,
  inReplyToMessageId,
  initialReplyMode = "reply",
  initialDraft,
  onDiscardDraft,
}: {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId: MessageId;
  initialReplyMode?: ReplyMode;
  initialDraft?: DraftResponse | null;
  onDiscardDraft?: () => void;
}) {
  const editorId = `message-input-${threadId}-${inReplyToMessageId}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const editorState = useMessageEditor("email", editorId);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [draftContent, setDraftContent] = useState<string>("");
  const hasLoadedDraft = useRef(false);

  const replyModeState = useReplyMode(initialReplyMode);

  const { data: draft } = useDraft({
    conversationId,
    threadId,
    inReplyToMessageId,
    initialData: initialDraft,
  });

  const { saveDraft, isSavingDraft, deleteDraft, isDeletingDraft } =
    useDraftMutations({ conversationId, threadId, inReplyToMessageId });

  const { sendMessage, isSending } = useSendMessage({ conversationId });

  const debouncedSaveDraft = useAsyncDebounce(
    useCallback(
      async (content: string, currentAttachments: Attachment[]) => {
        if (!hasLoadedDraft.current && draft) return;

        const plainText = editorState.getPlainText();
        const isEmpty = !plainText.trim() && currentAttachments.length === 0;
        if (isEmpty) return;

        await saveDraft({
          threadId,
          inReplyToMessageId,
          content,
          attachments: currentAttachments,
        });
      },
      [threadId, inReplyToMessageId, editorState, saveDraft, draft],
    ),
    500,
  );

  useEffect(() => {
    if (!draft || hasLoadedDraft.current) return;

    try {
      const parsedValue = JSON.parse(draft.content) as Value;
      editorState.setValue(parsedValue);
      setAttachments([...draft.attachments]);
      setDraftContent(draft.content);
      hasLoadedDraft.current = true;
    } catch {
      toast("Failed to load draft", { variant: "error" });
    }
  }, [draft, editorState, inReplyToMessageId]);

  const handleEditorChange = useHandler(({ value }: { value: Value }) => {
    const content = JSON.stringify(value);
    if (content === draftContent) return;
    setDraftContent(content);
    void debouncedSaveDraft(content, attachments);
  });

  const handleFileSelect = useHandler((files: File[]) => {
    // TODO: Upload files to S3 and get URLs
    // For now, just add placeholder attachments
    const newAttachments: Attachment[] = files.map((file) => ({
      id: MessageAttachmentId.generate(),
      url: URL.createObjectURL(file),
      description: Option.fromNullable(file.name),
      mimeType: Option.fromNullable(file.type),
    }));
    setAttachments((prev) => {
      const updated = [...prev, ...newAttachments];
      void debouncedSaveDraft(draftContent, updated);
      return updated;
    });
  });

  const handleRemoveAttachment = useCallback(
    (index: number) => {
      setAttachments((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        void debouncedSaveDraft(draftContent, updated);
        return updated;
      });
    },
    [draftContent, debouncedSaveDraft],
  );

  const handleSend = useHandler(async () => {
    if (!editorState.getPlainText() && attachments.length === 0) return;

    const html = await editorState.getHtml();

    await sendMessage({
      threadId,
      content: html,
      attachments,
      inReplyToMessageId,
      draftId: draft?.id,
    });

    editorState.clearContent();
    setAttachments([]);
    setDraftContent("");
    hasLoadedDraft.current = false;
    toast("Message sent", { variant: "success" });
  });

  const handleDeleteDraft = useHandler(async () => {
    if (!draft?.id) {
      onDiscardDraft?.();
      return;
    }

    await deleteDraft(draft.id);
    editorState.clearContent();
    setAttachments([]);
    setDraftContent("");
    hasLoadedDraft.current = false;
  });

  const sendAction = useSendMessageAction(handleSend);
  const deleteDraftAction = useDeleteDraftAction(handleDeleteDraft);

  useKeyboardShortcuts({
    bindings: useMemo(
      () => [
        { shortcut: messageInputShortcuts.SEND_MESSAGE, handler: handleSend },
        {
          shortcut: messageInputShortcuts.DELETE_DRAFT,
          handler: handleDeleteDraft,
        },
      ],
      [handleSend, handleDeleteDraft],
    ),
    scope: "messageInput",
    enabled: true,
    containerRef,
  });

  useActiveContext({
    id: `message-input-${conversationId}-${inReplyToMessageId}`,
    context: {
      conversationId,
      threadId,
      inReplyToMessageId,
      hasContent: !editorState.isEmpty,
      hasAttachments: attachments.length > 0,
      hasDraft: !!draft?.id,
    },
    handlers: {
      [conversationActionIds.sendMessage]: handleSend,
      [conversationActionIds.deleteDraft]: handleDeleteDraft,
      [conversationActionIds.reply]: () => replyModeState.setReplyMode("reply"),
      [conversationActionIds.replyAll]: () =>
        replyModeState.setReplyMode("replyAll"),
      [conversationActionIds.forward]: () =>
        replyModeState.setReplyMode("forward"),
    },
    rootScope: messageInputScope,
    enabled: true,
  });

  return (
    <div className="flex w-full flex-col py-4" ref={containerRef}>
      <MessageEditor
        actions={
          <MessageInputActions
            deleteDraftAction={deleteDraftAction}
            isDeletingDraft={isDeletingDraft}
            isSavingDraft={isSavingDraft}
            isSending={isSending}
            onDeleteDraft={handleDeleteDraft}
            onSend={handleSend}
            sendAction={sendAction}
          />
        }
        attachments={attachments}
        className="w-full"
        editorState={editorState}
        header={<ReplyModeDropdown replyModeState={replyModeState} />}
        onChange={handleEditorChange}
        onRemoveAttachment={handleRemoveAttachment}
        placeholder="Type your message..."
        toolbar={<EmailToolbar onFileSelect={handleFileSelect} />}
      />
    </div>
  );
});

const MessageInputActions = memo(function MessageInputActions({
  sendAction,
  deleteDraftAction,
  onSend,
  onDeleteDraft,
  isDeletingDraft,
  isSavingDraft,
  isSending,
}: {
  sendAction: ReturnType<typeof useSendMessageAction>;
  deleteDraftAction: ReturnType<typeof useDeleteDraftAction>;
  onSend: () => void;
  onDeleteDraft: () => void;
  isDeletingDraft: boolean;
  isSavingDraft: boolean;
  isSending: boolean;
}) {
  return (
    <>
      {deleteDraftAction?.shortcut ? (
        <Tooltip
          title={`${deleteDraftAction.title} (${formatShortcut(deleteDraftAction.shortcut)})`}
        >
          <RedoButton
            hierarchy="tertiary"
            IconLeading={deleteDraftAction.Icon}
            onClick={onDeleteDraft}
            pending={isDeletingDraft || isSavingDraft}
            size="sm"
            text=""
          />
        </Tooltip>
      ) : (
        <RedoButton
          hierarchy="tertiary"
          IconLeading={deleteDraftAction.Icon}
          onClick={onDeleteDraft}
          pending={isDeletingDraft || isSavingDraft}
          size="sm"
          text=""
        />
      )}
      {sendAction.shortcut ? (
        <Tooltip
          title={`${sendAction.title} (${formatShortcut(sendAction.shortcut)})`}
        >
          <RedoButton
            hierarchy="primary"
            IconLeading={sendAction.Icon}
            onClick={onSend}
            pending={isSending}
            size="md"
            text={sendAction.title}
            theme="normal"
          />
        </Tooltip>
      ) : (
        <RedoButton
          hierarchy="primary"
          IconLeading={sendAction.Icon}
          onClick={onSend}
          pending={isSending}
          size="md"
          text={sendAction.title}
          theme="normal"
        />
      )}
    </>
  );
});
